<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VButton, VButtonInvisible, VIcon, VLoadingSection } from '@/modules/shared/components'
import CapitalCallForm from '@/modules/investing/components/capital-call-form.vue'
import { useCapitalCallStore } from '@/modules/investing/stores/capital-call-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { newCapitalCallFromCommitments } from '@/modules/investing/utils/use-capital-call-form'

const route = useRoute()
const { entity_id, entity_type, slug } = route.params as { entity_id: string; entity_type: string; slug: string }
const router = useRouter()

const schedule_call_capital = route.query.schedule_call_capital
const capitalCallStore = useCapitalCallStore()
const entityStore = useEntityStore()

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const loading = ref(false)
const formComponent = ref(null)
const capitalCall = ref(null)

const entity = computed(() => entityStore.entity)
const commitments = computed(() => entityStore.listCommitments)

const submit = async (payload) => {
  loading.value = true
  await capitalCallStore.issueCapitalCall(payload, { slug })
  router.push({
    name: 'investing.transactions',
    params: { entity_id: entity_id, entity_type: entity_type },
  })
}
const triggerFormSubmit = () => {
  if (formComponent.value) formComponent.value.submit()
}

onMounted(async () => {
  showSkeleton()
  await entityStore.fetchCommitments(entity_id, { slug })

  // convert commitments to capital call transfers
  capitalCall.value = newCapitalCallFromCommitments(commitments.value, entity.value)
  hideSkeleton()
})
</script>

<template>
  <div class="fixed inset-0 overflow-y-auto bg-white">
    <!-- nav -->
    <div
      class="sticky top-0 z-50 flex items-center justify-between border-b bg-white/50 px-6 py-4 shadow-sm backdrop-blur"
    >
      <div class="left flex items-center space-x-3">
        <RouterLink
          :to="{
            name: 'investing.transactions',
            params: { entity_id: entity_id, entity_type: entity_type },
          }"
        >
          <VButtonInvisible><VIcon name="x" /></VButtonInvisible>
        </RouterLink>
        <h3 class="font-medium leading-7 text-gray-700 dark:text-gray-300">Call capital</h3>
      </div>
      <div class="right flex items-center space-x-1.5">
        <RouterLink
          :to="{
            name: 'investing.transactions',
            params: { entity_id: entity_id, entity_type: entity_type },
          }"
        >
          <VButton>Cancel</VButton>
        </RouterLink>
        <VButton :loading="loading" variant="primary" class="px-8" @click="triggerFormSubmit">Save call</VButton>
      </div>
    </div>
    <!-- main form -->
    <div class="p-6">
      <VLoadingSection
        v-if="skeleton"
        class="mt-40"
        title="Preparing Your Form..."
        description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
      smoothly."
      />
      <CapitalCallForm
        v-else
        ref="formComponent"
        :capital_call="capitalCall"
        :schedule_call_capital="schedule_call_capital == 'true'"
        :entity="entity"
        @submit="submit"
      />
    </div>
  </div>
</template>
